import React, { useState } from 'react';
import { Box, Card, CardContent, Typography, Chip, Menu, MenuItem, IconButton } from '@mui/material';
import { FaEllipsisV } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { formatTimeDuration } from '../../../utils/dateUtils';
import { getScopeBackgroundColor, getScopeTextColor, getScopeName } from '../../../constants/projectScopeTypes';
import { getClientPartyTypeName, getClientPartyTypeBackgroundColor, getClientPartyTypeTextColor } from '../../../constants/clientPartyTypes';
import { getStatusColor as getEmployeeStatusColor, getStatusTextColor as getEmployeeStatusTextColor, getStatusText as getEmployeeStatusText } from '../../../constants/employeeStatuses';
import { COLORS } from '../../../constants/radar';
import { useProjectPermissions } from '../../../hooks/useProjectPermissions';

dayjs.extend(utc);

const ProjectCard = ({ project }) => {
  const navigate = useNavigate();
  const { canManageProject, canManageSpecialProjects } = useProjectPermissions();
  const [menuAnchor, setMenuAnchor] = useState(null);

  const handleMenuOpen = (event) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleNavigation = (mode) => {
    navigate(`/projects/${project.id}/${mode}`);
    handleMenuClose();
  };

  return (
    <>
      <Card
        sx={{
          backgroundColor: '#f8f9fa',
          borderRadius: '4px',
          cursor: 'pointer',
          height: '320px',
          width: '100%',
          border: '1px solid #e0e0e0',
          boxShadow: 'none'
        }}
      >
        <CardContent sx={{ height: '100%', p: 2, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: COLORS.PRIMARY }}>
              {project.name}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {project.employeeData?.projectStatus && (
                <Chip
                  label={getEmployeeStatusText(project.employeeData.projectStatus)}
                  size="small"
                  sx={{
                    backgroundColor: getEmployeeStatusColor(project.employeeData.projectStatus),
                    color: getEmployeeStatusTextColor(project.employeeData.projectStatus),
                    fontWeight: 500,
                    fontSize: '12px'
                  }}
                />
              )}
              <IconButton
                size="small"
                onClick={handleMenuOpen}
                sx={{
                  padding: '4px',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                  }
                }}
              >
                <FaEllipsisV />
              </IconButton>
            </Box>
          </Box>

      {project.employeeData && (
        <>
          {project.employeeData.timeInProject && (
            <Box sx={{
              marginBottom: 1.5,
              color: '#64748B',
              fontSize: '13px',
              fontStyle: 'italic'
            }}>
              {formatTimeDuration(project.employeeData.timeInProject, 'on the Project')}
            </Box>
          )}

          <Box sx={{
            display: 'grid',
            gridTemplateColumns: '110px 1fr',
            gap: '8px',
            alignItems: 'start',
            fontSize: '13px',
            color: '#64748B',
            '& > *:nth-of-type(odd)': {
              fontWeight: 600,
              color: '#475569'
            }
          }}>
            {project.businessUnit && (
              <>
                <div>Business Unit:</div>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                  <span>{project.businessUnit.name}</span>
                  {project.businessUnit.cluster && (
                    <span style={{
                      fontSize: '11px',
                      color: '#6B7280',
                      backgroundColor: '#F3F4F6',
                      padding: '2px 6px',
                      borderRadius: '10px'
                    }}>
                      {project.businessUnit.cluster.name}
                    </span>
                  )}
                  {project.businessUnit.orgUnitType && (
                    <span style={{
                      fontSize: '11px',
                      color: '#6B7280',
                      backgroundColor: '#F3F4F6',
                      padding: '2px 6px',
                      borderRadius: '10px'
                    }}>
                      {project.businessUnit.orgUnitType.name}
                    </span>
                  )}
                </Box>
              </>
            )}

            {project.employeeData.team && (
              <>
                <div>Team:</div>
                <div>{project.employeeData.team.name}</div>
              </>
            )}

            {project.employeeData.engagementType && (
              <>
                <div>Engagement:</div>
                <div>{project.employeeData.engagementType.name}</div>
              </>
            )}

            {project.client && (
              <>
                <div>Client:</div>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <span>{project.client.name}</span>
                  {project.client.partyType && (
                    <Chip
                      label={getClientPartyTypeName(project.client.partyType)}
                      size="small"
                      sx={{
                        height: '14px',
                        fontSize: '10px',
                        fontWeight: 500,
                        backgroundColor: getClientPartyTypeBackgroundColor(project.client.partyType),
                        color: getClientPartyTypeTextColor(project.client.partyType),
                        '& .MuiChip-label': {
                          px: 0.5
                        }
                      }}
                    />
                  )}
                </Box>
              </>
            )}

            {project.scope && (
              <>
                <div>Scope:</div>
                <Box>
                  <Chip
                    label={getScopeName(project.scope)}
                    size="small"
                    sx={{
                      height: '16px',
                      fontSize: '10px',
                      fontWeight: 500,
                      backgroundColor: getScopeBackgroundColor(project.scope),
                      color: getScopeTextColor(project.scope)
                    }}
                  />
                </Box>
              </>
            )}

            {project.projectPurpose && (
              <>
                <div>Purpose:</div>
                <Box>
                  <Chip
                    label={project.projectPurpose.name}
                    size="small"
                    sx={{
                      height: '16px',
                      fontSize: '10px',
                      fontWeight: 500,
                      backgroundColor: getScopeBackgroundColor(project.scope),
                      color: getScopeTextColor(project.scope),
                      '& .MuiChip-label': {
                        px: 0.5
                      }
                    }}
                  />
                </Box>
              </>
            )}

            {project.employeeData.roles && project.employeeData.roles.length > 0 && (
              <>
                <div>Roles:</div>
                <div>{project.employeeData.roles.map(role => role.label).join(', ')}</div>
              </>
            )}

            {project.employeeData.startDate && (
              <>
                <div>Start Date:</div>
                <div>{dayjs.utc(project.employeeData.startDate).format('DD/MM/YYYY')}</div>
              </>
            )}

            {project.employeeData.endDate && (
              <>
                <div>End Date:</div>
                <div>{dayjs.utc(project.employeeData.endDate).format('DD/MM/YYYY')}</div>
              </>
            )}
          </Box>
        </>
      )}
    </CardContent>
  </Card>

  <Menu
    anchorEl={menuAnchor}
    open={Boolean(menuAnchor)}
    onClose={handleMenuClose}
  >
    <MenuItem onClick={() => handleNavigation('view')}>
      View
    </MenuItem>
    {canManageProject(project) && (
      <MenuItem onClick={() => handleNavigation('edit')}>
        Edit
      </MenuItem>
    )}
    {canManageSpecialProjects(project) && (
      <MenuItem onClick={() => handleNavigation('manage-employees')}>
        Manage Individuals
      </MenuItem>
    )}
  </Menu>
</>
  );
};

export default ProjectCard;
